import React, { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '../../stores';
import './Admin.css';

interface CryptoWallet {
  id: number;
  name: string;
  symbol: string;
  network: string;
  address: string;
  qrCodeUrl?: string;
  creditsPerUnit: number;
  description?: string;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface CryptoWalletFormData {
  name: string;
  symbol: string;
  network: string;
  address: string;
  qrCodeUrl: string;
  creditsPerUnit: string;
  description: string;
  isActive: boolean;
}

const SYMBOL_OPTIONS = [
  { value: 'USDT', label: 'USDT (Tether)' },
  { value: 'USDC', label: 'USDC' },
  { value: 'ETH', label: 'ETH' },
  { value: 'BTC', label: 'BTC' },
  { value: 'TRX', label: 'TRX' },
];

const NETWORK_OPTIONS = [
  { value: 'ERC20', label: 'ERC20 (Ethereum)' },
  { value: 'TRC20', label: 'TRC20 (Tron)' },
  { value: 'BEP20', label: 'BEP20 (BSC)' },
  { value: 'SOL', label: 'Solana' },
  { value: 'native', label: 'Native' },
];

const defaultFormData: CryptoWalletFormData = {
  name: '',
  symbol: 'USDT',
  network: 'TRC20',
  address: '',
  qrCodeUrl: '',
  creditsPerUnit: '1',
  description: '',
  isActive: true,
};

export function CryptoWallets() {
  const { accessToken } = useAuthStore();
  const [wallets, setWallets] = useState<CryptoWallet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingWallet, setEditingWallet] = useState<CryptoWallet | null>(null);
  const [formData, setFormData] = useState<CryptoWalletFormData>(defaultFormData);

  const fetchWallets = useCallback(async () => {
    try {
      const response = await fetch('/api/admin/crypto-wallets', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) throw new Error('Failed to fetch crypto wallets');
      const data = await response.json();
      setWallets(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load crypto wallets');
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    fetchWallets();
  }, [fetchWallets]);

  const handleOpenModal = (wallet?: CryptoWallet) => {
    if (wallet) {
      setEditingWallet(wallet);
      setFormData({
        name: wallet.name,
        symbol: wallet.symbol,
        network: wallet.network,
        address: wallet.address,
        qrCodeUrl: wallet.qrCodeUrl ?? '',
        creditsPerUnit: String(wallet.creditsPerUnit),
        description: wallet.description ?? '',
        isActive: wallet.isActive,
      });
    } else {
      setEditingWallet(null);
      setFormData(defaultFormData);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingWallet(null);
    setFormData(defaultFormData);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setActionLoading(editingWallet?.id ?? -1);

    try {
      const url = editingWallet
        ? `/api/admin/crypto-wallets/${editingWallet.id}`
        : '/api/admin/crypto-wallets';
      const method = editingWallet ? 'PUT' : 'POST';
      const body = JSON.stringify({
        name: formData.name,
        symbol: formData.symbol,
        network: formData.network,
        address: formData.address,
        qrCodeUrl: formData.qrCodeUrl || undefined,
        creditsPerUnit: parseFloat(formData.creditsPerUnit) || 1,
        description: formData.description || undefined,
        isActive: formData.isActive,
      });

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body,
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to save crypto wallet');
      }
      handleCloseModal();
      await fetchWallets();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggle = async (wallet: CryptoWallet) => {
    setActionLoading(wallet.id);
    try {
      const response = await fetch(`/api/admin/crypto-wallets/${wallet.id}/toggle`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) throw new Error('Toggle failed');
      await fetchWallets();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (wallet: CryptoWallet) => {
    if (!confirm(`"${wallet.name}" cüzdanını silmek istediğinize emin misiniz?`)) return;
    setActionLoading(wallet.id);
    try {
      const response = await fetch(`/api/admin/crypto-wallets/${wallet.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) throw new Error('Delete failed');
      await fetchWallets();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    } finally {
      setActionLoading(null);
    }
  };

  const maskAddress = (addr: string): string => {
    if (!addr || addr.length < 12) return '••••••••';
    return `${addr.slice(0, 8)}...${addr.slice(-8)}`;
  };

  if (loading) return <div className="admin-loading">Yükleniyor...</div>;

  return (
    <div className="admin-page">
      <header className="admin-page-header">
        <div>
          <h1>Crypto Wallets</h1>
          <p>Kredi yükleme için kripto cüzdan adreslerini yönetin (USDT, vs.)</p>
        </div>
        <button className="admin-btn admin-btn-primary" onClick={() => handleOpenModal()}>
          + Yeni Cüzdan Ekle
        </button>
      </header>

      {error && <div className="admin-error">{error}</div>}

      <section className="admin-table-section">
        <div className="admin-table-toolbar">
          <h2>Crypto Wallets ({wallets.length})</h2>
        </div>

        {wallets.length === 0 ? (
          <div className="admin-empty-state">
            <p>Henüz cüzdan eklenmemiş.</p>
            <button className="admin-btn admin-btn-primary" onClick={() => handleOpenModal()}>
              İlk Cüzdanı Ekle
            </button>
          </div>
        ) : (
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Ad</th>
                  <th>Token</th>
                  <th>Ağ</th>
                  <th>Adres</th>
                  <th>Kredi/1 Ünit</th>
                  <th>Durum</th>
                  <th>Açıklama</th>
                  <th>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {wallets.map((wallet) => (
                  <tr key={wallet.id}>
                    <td>{wallet.name}</td>
                    <td><span className="provider-type-badge">{wallet.symbol}</span></td>
                    <td>{wallet.network}</td>
                    <td><code className="admin-code-inline" title={wallet.address}>{maskAddress(wallet.address)}</code></td>
                    <td>{wallet.creditsPerUnit}</td>
                    <td>
                      <button
                        type="button"
                        className={`admin-toggle-btn ${wallet.isActive ? 'active' : ''}`}
                        onClick={() => handleToggle(wallet)}
                        disabled={actionLoading === wallet.id}
                        title={wallet.isActive ? 'Deaktif Et' : 'Aktif Et'}
                      >
                        {wallet.isActive ? '✓ Aktif' : '✗ Pasif'}
                      </button>
                    </td>
                    <td>{wallet.description || '-'}</td>
                    <td>
                      <div className="admin-action-buttons">
                        <button
                          className="admin-edit-btn"
                          onClick={() => handleOpenModal(wallet)}
                          disabled={actionLoading === wallet.id}
                          title="Düzenle"
                        >
                          ✏️
                        </button>
                        <button
                          className="admin-delete-btn"
                          onClick={() => handleDelete(wallet)}
                          disabled={actionLoading === wallet.id}
                          title="Sil"
                        >
                          {actionLoading === wallet.id ? '...' : '🗑'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {showModal && (
        <div className="admin-modal-overlay" onClick={handleCloseModal}>
          <div className="admin-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>{editingWallet ? 'Cüzdan Düzenle' : 'Yeni Cüzdan Ekle'}</h2>
              <button className="admin-modal-close" onClick={handleCloseModal}>✕</button>
            </div>
            <form onSubmit={handleSubmit} className="admin-form">
              <div className="admin-form-group">
                <label className="admin-form-label">
                  Ad *
                  <input
                    type="text"
                    className="admin-form-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="Örn: Tether TRC20 Ana"
                  />
                </label>
              </div>
              <div className="admin-form-group">
                <label className="admin-form-label">
                  Token
                  <select
                    className="admin-form-select"
                    value={formData.symbol}
                    onChange={(e) => setFormData({ ...formData, symbol: e.target.value })}
                  >
                    {SYMBOL_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="admin-form-group">
                <label className="admin-form-label">
                  Ağ
                  <select
                    className="admin-form-select"
                    value={formData.network}
                    onChange={(e) => setFormData({ ...formData, network: e.target.value })}
                  >
                    {NETWORK_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="admin-form-group">
                <label className="admin-form-label">
                  Cüzdan Adresi *
                  <input
                    type="text"
                    className="admin-form-input"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    required
                    placeholder="T... veya 0x..."
                  />
                </label>
              </div>
              <div className="admin-form-group">
                <label className="admin-form-label">
                  QR Kod URL (opsiyonel)
                  <input
                    type="text"
                    className="admin-form-input"
                    value={formData.qrCodeUrl}
                    onChange={(e) => setFormData({ ...formData, qrCodeUrl: e.target.value })}
                    placeholder="https://..."
                  />
                </label>
              </div>
              <div className="admin-form-group">
                <label className="admin-form-label">
                  Kredi / 1 Ünit (1 USDT = X kredi)
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    className="admin-form-input"
                    value={formData.creditsPerUnit}
                    onChange={(e) => setFormData({ ...formData, creditsPerUnit: e.target.value })}
                    placeholder="1"
                  />
                </label>
              </div>
              <div className="admin-form-group">
                <label className="admin-form-label">
                  Açıklama
                  <textarea
                    className="admin-form-textarea"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Örn: Sadece USDT TRC20 için"
                    rows={2}
                  />
                </label>
              </div>
              <div className="admin-form-group">
                <label className="admin-setting-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                  <span>Aktif (Kredilerde göster)</span>
                </label>
              </div>
              <div className="admin-form-actions">
                <button type="button" className="admin-btn admin-btn-secondary" onClick={handleCloseModal} disabled={actionLoading !== null}>
                  İptal
                </button>
                <button type="submit" className="admin-btn admin-btn-primary" disabled={actionLoading !== null}>
                  {actionLoading !== null ? 'Kaydediliyor...' : editingWallet ? 'Güncelle' : 'Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
